/**
 * Dashboard Page
 *
 * Comprehensive analytics and overview of the platform
 */
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useMemo, memo, ElementType, useRef } from 'react'
import OrchestratorComposer, { ComposerHandle } from '@/components/orchestrator/OrchestratorComposer'
import RecentOrchestratorRuns from '@/components/orchestrator/RecentOrchestratorRuns'
import {
  CpuChipIcon,
  Square3Stack3DIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UsersIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  BoltIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'
import Button from '@/components/common/Button'
import { apiClient } from '@/services/api'
import { formatDistanceToNow } from 'date-fns'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'

/**
 * MetricCard — matches internal-planner's card style:
 *   - Icon in a colored pill (top-right)
 *   - Title top-left (muted)
 *   - Big bold value below
 *   - Optional trend line
 *   - Clickable variant with hover scale
 */
interface MetricCardProps {
  title: string
  value: string | number
  icon: ElementType
  /** Tailwind bg-* class for the icon pill, e.g. 'bg-violet-500' */
  color: string
  trend?: string
  onClick?: () => void
}

const MetricCard = memo(function MetricCard({ title, value, icon: Icon, color, trend, onClick }: MetricCardProps) {
  return (
    <div
      onClick={onClick}
      className={`bg-card rounded-xl border border-border p-6 transition-all duration-200 ${
        onClick ? 'hover:shadow-lg hover:scale-[1.03] cursor-pointer hover:border-primary' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="text-3xl font-bold text-foreground mb-1">{value}</div>
      {trend && <p className="text-sm text-muted-foreground">{trend}</p>}
      {onClick && <p className="text-xs text-primary mt-2">Click to view &rarr;</p>}
    </div>
  )
})

const DashboardPage = observer(() => {
  const navigate = useNavigate()
  const composerRef = useRef<ComposerHandle>(null)

  // Execution stats now come from audit stats (ground truth) — see effectiveStats below
  const isLoadingStats = false

  // Fetch recent executions - poll in background without triggering loading states
  const { data: executions, isLoading: isLoadingExecutions } = useQuery({
    queryKey: ['recent-executions'],
    queryFn: () => apiClient.getExecutions({ limit: 10 }),
    refetchInterval: 30000, // Refresh every 30 seconds (reduced from 10)
    staleTime: 15000, // Consider data fresh for 15 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus to reduce re-renders
  })

  // Fetch custom agents
  const { data: agents, isLoading: isLoadingAgents } = useQuery({
    queryKey: ['agents-count'],
    queryFn: () => apiClient.getAgents({ limit: 100 }),
    staleTime: 60000,
  })

  // Fetch centralized (internal) agents
  const { data: centralizedAgents } = useQuery({
    queryKey: ['centralized-agents-count'],
    queryFn: () => apiClient.getCentralizedAgents(),
    staleTime: 60000,
  })

  // Combined agent count
  const totalAgentCount = (agents?.total || 0) + (centralizedAgents?.length || 0)

  // Fetch teams + crews for combined count
  const { data: teams } = useQuery({
    queryKey: ['teams-count'],
    queryFn: () => apiClient.getTeams({ limit: 100 }),
    staleTime: 60000,
  })

  const { data: crews } = useQuery({
    queryKey: ['crews-count'],
    queryFn: async () => {
      try {
        const resp = await (apiClient as any).client.get('/api/v1/crews/')
        return Array.isArray(resp.data) ? resp.data : resp.data?.items || []
      } catch { return [] }
    },
    staleTime: 60000,
  })

  const totalTeamsCount = (teams?.total || 0) + (Array.isArray(crews) ? crews.length : 0)

  // Fetch workflows - user-created (DB) + builtin graphs (backend)
  const { data: workflows, isLoading: isLoadingWorkflows } = useQuery({
    queryKey: ['workflows-count'],
    queryFn: () => apiClient.getWorkflows({ limit: 100 }),
    staleTime: 60000,
  })
  const { data: builtinGraphsSummary } = useQuery({
    queryKey: ['builtin-graphs-count'],
    queryFn: async () => {
      const resp = await (apiClient as any).client.get('/api/v1/workflows/graphs/summary')
      return resp.data?.workflows || []
    },
    staleTime: 60000,
  })
  const totalWorkflowCount = (workflows?.total || 0) + (Array.isArray(builtinGraphsSummary) ? builtinGraphsSummary.length : 0)

  // Fetch knowledge graphs - rarely changes
  const { data: knowledgeGraphs } = useQuery({
    queryKey: ['graphs-count'],
    queryFn: () => apiClient.getKnowledgeGraphs({ limit: 100 }),
    staleTime: 60000,
  })

  const isLoading = isLoadingStats || isLoadingExecutions || isLoadingAgents || isLoadingWorkflows

  // Chart data from audit stats (real execution data — ground truth)
  const { data: auditStats } = useQuery({
    queryKey: ['audit-stats-dashboard'],
    queryFn: async () => {
      try {
        const resp = await (apiClient as any).client.get('/api/v1/audit/stats')
        return resp.data
      } catch { return null }
    },
    staleTime: 60000,
  })

  // Transform audit stats into chart-friendly arrays
  const costByTypeData = useMemo(() => {
    if (!auditStats?.agents) return []
    const agents = auditStats.agents as Record<string, any>
    const entries = Object.entries(agents)
      .map(([name, data]: [string, any]) => ({
        name: name.replace(/-/g, ' '),
        cost: data.total_cost_usd || 0,
        percentage: 0,
      }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 6)
    const total = entries.reduce((s, e) => s + e.cost, 0) || 1
    return entries.map(e => ({ ...e, percentage: Math.round((e.cost / total) * 100) }))
  }, [auditStats])

  const executionTrendData = useMemo(() => {
    if (!auditStats?.agents) return []
    // Build per-agent execution counts as a bar chart
    const agents = auditStats.agents as Record<string, any>
    return Object.entries(agents)
      .map(([name, data]: [string, any]) => ({
        date: name.replace(/-/g, ' ').slice(0, 12),
        total: data.executions || 0,
        success: data.successful || 0,
        failed: (data.executions || 0) - (data.successful || 0),
        running: 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 7)
  }, [auditStats])

  const tokenUsageData = useMemo(() => {
    if (!auditStats?.agents) return []
    const agents = auditStats.agents as Record<string, any>
    return Object.entries(agents)
      .map(([name, data]: [string, any]) => ({
        date: name.replace(/-/g, ' ').slice(0, 12),
        tokens: data.total_tokens || 0,
      }))
      .sort((a, b) => b.tokens - a.tokens)
      .slice(0, 7)
  }, [auditStats])

  // Audit stats is the ground truth for all execution metrics
  const effectiveStats = useMemo(() => {
    if (!auditStats) return null
    return {
      total_executions: auditStats.total_executions || 0,
      completed: auditStats.successful_executions || 0,
      failed: auditStats.failed_executions || 0,
      success_rate: auditStats.success_rate || 0,
      average_duration_ms: auditStats.avg_duration_ms || 0,
      total_cost: auditStats.total_cost_usd || 0,
    }
  }, [auditStats])

  // Framework distribution - memoized with dependency on agents
  const frameworkData = useMemo(() => [
    { name: 'LangChain', value: agents?.items?.filter(a => a.framework === 'langchain').length || 0 },
    { name: 'CrewAI', value: agents?.items?.filter(a => a.framework === 'crewai').length || 0 },
    { name: 'ADK', value: agents?.items?.filter(a => a.framework === 'adk').length || 0 },
    { name: 'Custom', value: agents?.items?.filter(a => a.framework === 'custom').length || 0 },
  ], [agents?.items])

  const COLORS = useMemo(() => ['#3b82f6', '#a855f7', '#22c55e', '#f59e0b'], [])

  // Memoize computed statistics for performance metrics
  const activeAgentsCount = useMemo(() =>
    agents?.items?.filter(a => a.status === 'active').length || 0
  , [agents?.items])

  const activeAgentsPercentage = useMemo(() =>
    (activeAgentsCount / (agents?.total || 1)) * 100
  , [activeAgentsCount, agents?.total])

  const successRatePercent = useMemo(() =>
    (effectiveStats?.success_rate || 0) * 100
  , [effectiveStats?.success_rate])

  // Memoize helper functions to prevent recreation
  const getStatusIcon = useMemo(() => (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      case 'running':
        return <PlayIcon className="w-5 h-5 text-indigo-500 animate-pulse" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }, [])

  const getStatusBadge = useMemo(() => (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      case 'failed':
        return <Badge variant="error">Failed</Badge>
      case 'running':
        return <Badge variant="primary">Running</Badge>
      case 'queued':
        return <Badge variant="warning">Queued</Badge>
      default:
        return <Badge variant="gray">{status}</Badge>
    }
  }, [])

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Loading your dashboard...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-muted rounded w-3/4"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Real-time overview of your AI agent platform</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/teams')}>
            Create Team
          </Button>
          <Button variant="primary" onClick={() => navigate('/agents')}>
            Create Agent
          </Button>
        </div>
      </div>

      {/* Orchestrator composer + recent-runs rail.
          Per PLAN-orchestrator-ux.md Phases A + C. Composer takes 2/3 width,
          the rail sits on the right at 1/3 width. Click a row to re-prefill
          the composer prompt; the small launch icon opens the trace replay
          in Playground. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <OrchestratorComposer ref={composerRef} />
        </div>
        <div>
          <RecentOrchestratorRuns
            onPickPrompt={(p) => p && composerRef.current?.setPrompt(p)}
          />
        </div>
      </div>

      {/* Primary Stats — planner-style MetricCards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Agents"
          value={totalAgentCount}
          icon={CpuChipIcon}
          color="bg-violet-500"
          trend={`${totalAgentCount} registered`}
          onClick={() => navigate('/agents')}
        />
        <MetricCard
          title="Executions"
          value={effectiveStats?.total_executions || 0}
          icon={PlayIcon}
          color="bg-emerald-500"
          trend={`${((effectiveStats?.success_rate || 0) * 100).toFixed(0)}% success rate`}
          onClick={() => navigate('/operations')}
        />
        <MetricCard
          title="Active Teams"
          value={totalTeamsCount}
          icon={UsersIcon}
          color="bg-cyan-500"
          trend={`${totalTeamsCount} teams configured`}
          onClick={() => navigate('/agent-teams')}
        />
        <MetricCard
          title="Cost (30d)"
          value={`$${(effectiveStats?.total_cost || 0).toFixed(2)}`}
          icon={CurrencyDollarIcon}
          color="bg-amber-500"
          trend={`Avg ${((effectiveStats?.average_duration_ms || 0) / 1000).toFixed(1)}s per run`}
        />
      </div>

      {/* Secondary Stats — matching MetricCard style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Workflows"
          value={totalWorkflowCount}
          icon={Square3Stack3DIcon}
          color="bg-purple-500"
          onClick={() => navigate('/workflows')}
        />
        <MetricCard
          title="Knowledge Graphs"
          value={knowledgeGraphs?.total || 0}
          icon={ChartBarIcon}
          color="bg-blue-500"
        />
        <MetricCard
          title="Success Rate"
          value={`${((effectiveStats?.success_rate || 0) * 100).toFixed(1)}%`}
          icon={CheckCircleIcon}
          color="bg-green-500"
        />
        <MetricCard
          title="Avg Duration"
          value={`${((effectiveStats?.average_duration_ms || 0) / 1000).toFixed(2)}s`}
          icon={BoltIcon}
          color="bg-orange-500"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Execution Trend Area Chart */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Execution Trend (7 Days)</h3>
            <Badge variant="primary">Live</Badge>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={executionTrendData}>
              <defs>
                <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="success"
                stroke="#22c55e"
                fillOpacity={1}
                fill="url(#colorSuccess)"
                strokeWidth={2}
                name="Successful"
              />
              <Area
                type="monotone"
                dataKey="failed"
                stroke="#ef4444"
                fillOpacity={1}
                fill="url(#colorFailed)"
                strokeWidth={2}
                name="Failed"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Token Usage Trend */}
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4">Token Usage (7 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tokenUsageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
                formatter={(value: any) => [`${value.toLocaleString()} tokens`, 'Tokens']}
              />
              <Bar dataKey="tokens" fill="#3b82f6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Breakdown */}
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4">Cost by Agent Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costByTypeData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" fontSize={12} />
              <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={12} width={80} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
                formatter={(value: any) => [`$${value.toFixed(2)}`, 'Cost']}
              />
              <Bar dataKey="cost" fill="#22c55e" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Framework Distribution */}
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4">Framework Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={frameworkData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {frameworkData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Activity Monitor */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Live Activity
              </h3>
              <span className="text-xs text-muted-foreground">Auto-refreshes every 30s</span>
            </div>
            {auditStats?.agents ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(auditStats.agents as Record<string, any>)
                  .sort(([, a]: any, [, b]: any) => (b.executions || 0) - (a.executions || 0))
                  .slice(0, 10)
                  .map(([name, data]: [string, any]) => (
                    <div key={name} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${data.success_rate >= 0.9 ? 'bg-green-500' : data.success_rate >= 0.7 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{name.replace(/-/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">
                            {data.executions} runs | {((data.success_rate || 0) * 100).toFixed(0)}% success | {((data.avg_duration_ms || 0) / 1000).toFixed(1)}s avg
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-foreground">{(data.total_tokens || 0).toLocaleString()} tokens</p>
                        <p className="text-xs text-muted-foreground">${(data.total_cost_usd || 0).toFixed(4)}</p>
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">No agent activity yet</p>
            )}
          </Card>
        </div>

        {/* Recent Executions */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Recent Executions</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/executions')}>
                View all
              </Button>
            </div>

            {executions?.items && executions.items.length > 0 ? (
              <div className="space-y-2">
                {executions.items.slice(0, 5).map((execution) => (
                  <div
                    key={execution.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate(`/executions`)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {getStatusIcon(execution.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          Agent Execution #{execution.id.slice(0, 8)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(execution.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {execution.duration_ms && (
                        <span className="text-xs text-muted-foreground font-mono">
                          {(execution.duration_ms / 1000).toFixed(2)}s
                        </span>
                      )}
                      {execution.cost && (
                        <span className="text-xs text-muted-foreground font-mono">
                          ${execution.cost.toFixed(4)}
                        </span>
                      )}
                      {getStatusBadge(execution.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <PlayIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">No executions yet</p>
                <p className="text-sm mt-1">Run your first agent to see activity here</p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate('/agents')}
                >
                  Create Agent
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="space-y-4">
          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-3">Performance Metrics</h3>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Success Rate</span>
                  <span className="text-xs font-semibold text-foreground">
                    {successRatePercent.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${successRatePercent}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Active Agents</span>
                  <span className="text-xs font-semibold text-foreground">
                    {activeAgentsCount}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${activeAgentsPercentage}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Success Rate</span>
                  <span className="text-xs font-semibold text-foreground">{((effectiveStats?.success_rate || 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-amber-500 h-2 rounded-full transition-all duration-500" style={{ width: `${(effectiveStats?.success_rate || 0) * 100}%` }}></div>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/agents')}
              >
                <CpuChipIcon className="w-4 h-4 mr-2" />
                Create Agent
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/teams')}
              >
                <UsersIcon className="w-4 h-4 mr-2" />
                Create Team
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/workflows')}
              >
                <Square3Stack3DIcon className="w-4 h-4 mr-2" />
                Build Workflow
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/knowledge-graphs')}
              >
                <ChartBarIcon className="w-4 h-4 mr-2" />
                Create Graph
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default DashboardPage
